import { motion } from "framer-motion";
import { Brain, CheckCircle, Settings, Palette } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import hooolyIndoorAsset from "@/assets/hoooly-indoor-new.png.asset.json";
import hooolySmallAsset from "@/assets/hoooly-small-new.png.asset.json";
const hooolyIndoor = hooolyIndoorAsset.url;
const hooolySmall = hooolySmallAsset.url;
import hooolyFoodAsset from "@/assets/hoooly-food-new.png.asset.json";
const hooolyFood = hooolyFoodAsset.url;
import dashboardImg from "@/assets/ganiga-smart-waste-management-dashboard.png";

export const ProductsSection = () => {
  const { t } = useLanguage();

  const products = [
    {
      id: "indoor",
      name: t.products.indoor.name,
      tagline: t.products.indoor.tagline,
      image: hooolyIndoor,
      specs: t.products.indoor.specs,
      idealFor: t.products.indoor.idealFor,
    },
    {
      id: "small",
      name: t.products.small.name,
      tagline: t.products.small.tagline,
      image: hooolySmall,
      specs: t.products.small.specs,
      idealFor: t.products.small.idealFor,
    },
    {
      id: "food",
      name: t.products.food.name,
      tagline: t.products.food.tagline,
      image: hooolyFood,
      specs: t.products.food.specs,
      idealFor: t.products.food.idealFor,
    },
    {
      id: "dashboard",
      name: t.products.dashboard.name,
      tagline: t.products.dashboard.tagline,
      image: dashboardImg,
      specs: t.products.dashboard.specs,
      idealFor: t.products.dashboard.idealFor,
    },
  ];

  const techFeatures = [
    {
      icon: Brain,
      ...t.products.techFeatures.aiRecognition,
    },
    {
      icon: CheckCircle,
      ...t.products.techFeatures.sorting,
    },
    {
      icon: Settings,
      ...t.products.techFeatures.continuity,
    },
    {
      icon: Palette,
      ...t.products.techFeatures.design,
    },
  ];

  return (
    <section id="products" className="py-32 lg:py-40 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
            {t.products.title} <span className="text-primary">{t.products.titleHighlight}</span>
          </h2>
          <p className="text-muted-foreground font-light max-w-2xl mx-auto text-lg">{t.products.subtitle}</p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="h-full bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-xl flex flex-col">
                {/* Product Image */}
                <div className={`relative aspect-[16/9] overflow-hidden ${
                  product.id === "indoor" || product.id === "small" ? "bg-white" : "bg-muted/30"
                }`}>
                  <img
                    src={product.image}
                    alt={`${product.name} - ${product.id === 'indoor' ? 'AI smart bin for automatic waste sorting in food courts and airports' : product.id === 'small' ? 'Smart waste bin with AI sorting for offices and retail' : product.id === 'food' ? 'AI food waste recognition identifying cooked vegetables on a plate' : 'Smart waste management dashboard with ESG reporting and analytics'}`}
                    className={`w-full h-full transition-transform duration-700 group-hover:scale-105 ${
                      product.id === "indoor" || product.id === "small" ? "object-contain" : product.id === "food" ? "object-cover object-top" : "object-cover"
                    }`}
                  />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1.5 text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground rounded-full border border-border">
                      {product.idealFor}
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-6 lg:p-8 space-y-4 flex-1 flex flex-col">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">{product.name}</h3>
                    <p className="text-muted-foreground font-light">{product.tagline}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {product.specs.map((spec) => (
                      <span
                        key={spec}
                        className="px-3 py-1.5 text-xs bg-muted rounded-full text-muted-foreground font-medium"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Technology Sub-section */}
        <motion.div
          id="technology"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-32 scroll-mt-24"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
              {t.products.techTitle} <span className="text-primary">{t.products.techTitleHighlight}</span> {t.products.techTitleEnd}
            </h3>
            <p className="text-lg text-muted-foreground font-light max-w-2xl mx-auto">
              {t.products.techSubtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 max-w-6xl mx-auto">
            {techFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="group"
              >
                <div className="h-full bg-card rounded-2xl p-6 border border-border hover:border-primary/40 transition-all duration-500 hover:shadow-lg hover:-translate-y-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-right">
                      <span className="text-2xl lg:text-3xl font-bold text-primary">{feature.stat}</span>
                      <p className="text-xs text-muted-foreground">{feature.statLabel}</p>
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-foreground mb-2">{feature.title}</h4>
                  <p className="text-muted-foreground font-light text-sm leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
